import { Descriptions, Select } from "antd";
import moment from "moment";
import { FC, useEffect, useState } from "react";
import { CgCloseR } from "react-icons/cg";
import { useSelector } from "react-redux";
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation } from "react-router-dom";
import useAuth from "../../../hooks/useAuth";
import useNotification from "../../../hooks/useNotification";
import { IEducationArticle } from "../../../models/homepageManagementEducationInfo.model";
import { RootState } from "../../../store/rootReducer";
import Button from "../../Button";
import FileUpload from "../../FileUpload";
import Tinymce from "../../Tinymce";

export interface IProps {
  data?: IEducationArticle;
  submit: (data: FormData) => void;
}

const EducationEditor: FC<IProps> = ({
  data, submit
}) => {
  const { t } = useTranslation();
  const { writeAuthYn } = useAuth();
  const history = useHistory();
  const location = useLocation();  
  const { infoNotification } = useNotification();
  const { serverDate } = useSelector((state: RootState) => state.serverDate);
  const { commonCodes } = useSelector((state: RootState) => state.commonCode);
  // BR0014 = 정보게시판 구분
  const BR0014 = commonCodes.find(item => item.commonCode === 'BR0014')?.detailCode;
  // BR0015 = 교육정보 분류
  const BR0015 = commonCodes.find(item => item.commonCode === 'BR0015')?.detailCode;
  // BR0016 = 영상정보 분류
  const BR0016 = commonCodes.find(item => item.commonCode === 'BR0016')?.detailCode;

  const [title, setTitle] = useState('');
  const [bulletinDiv, setBulletinDiv] = useState<string>();
  const [bulletinBrn, setBulletinBrn] = useState<string>();
  const [url, setUrl] = useState('');
  const [content, setContent] = useState('');
  const [file, setFile] = useState<File>();
  const [thumbnail, setThumbnail] = useState<any>();
  const [isCreate, setIsCreate] = useState(true);

  useEffect(() => {
    if (data) {
      setTitle(data.title);
      setContent(data.content);
      setBulletinDiv(data.bulletinDiv);
      setBulletinBrn(data.bulletinBrn);
      setUrl(data.url);
      setThumbnail(data.thumbnail);
      setIsCreate(false);
    }
  }, [data]);

  const onSubmit = () => {
    if (!title) {
      return infoNotification(t('homepage.archive.message1'));
    }
    if (!content) {
      return infoNotification(t('homepage.archive.message4'));
    }
    if (!bulletinDiv) {
      return infoNotification(t('homepage.education.message1'));
    }
    if (!bulletinBrn) {
      return infoNotification(t('homepage.education.message2'));
    }
    if (bulletinDiv === 'MO' && !url) {
      return infoNotification(t('homepage.education.message3'));
    }
    if ((isCreate && !file) || (!isCreate && !file && !thumbnail?.originalName)) {
      return infoNotification(t('homepage.education.message4'));
    }

    const form = new FormData();
    form.append('title', title);
    form.append('content', content);
    form.append('bulletinDiv', bulletinDiv);
    form.append('bulletinBrn', bulletinBrn);
    if (url) form.append('url', url);

    if (file) {
      if (isCreate) {
        form.append('thumbnailFile', file);
      } else {
        form.append('thumbnailFileStatus', 'update');
        form.append('thumbnailNewFile', file);
      }
    } else if (!isCreate) {
      form.append('thumbnailFileStatus', 'keep');
    }

    if (isCreate) {
      /* 생성 시 파일 처리 */
      if (file) form.append('thumbnail', file);
    } else {
      /* 수정 시 파일 처리 */
      if (data?.attachFileCode) {
        form.append('attachFileCode', String(data.attachFileCode));
      }
      if (file) {
        form.append('newThumbnail', file);
      }

      if (thumbnail) {
        // 기존 첨부파일 있음
        if (file) form.append('fileStatus', 'update');
        else if (!thumbnail.originalName) form.append('fileStatus', 'delete');
        else form.append('fileStatus', 'keep');
      } else {
        // 기존 첨부파일 없음
        if (file) form.append('fileStatus', 'create');
        else form.append('fileStatus', 'keep');
      }
    }

    submit(form);
  }

  const onSetBulletinDiv = (value: string) => {
    setBulletinDiv(value);
    setBulletinBrn(undefined);
  }

  return (
    <div className='comm-table__area rowType mt-0'>
      <Descriptions
        bordered
        column={3}
        size="small"
        labelStyle={{ width: '16.5%' }}
        contentStyle={{ width: '16.5%' }}
      >
        <Descriptions.Item label={`* ${t('common.column.title')}`} span={3}>
          <input
            type="text"
            className="form-control"
            value={title}
            onChange={e => setTitle(e.target.value)}
          />
        </Descriptions.Item>
        <Descriptions.Item label={`* ${t('homepage.education.column2')}`}>
          <div className="w-150p">
            <Select
              className="w-100"
              placeholder={t('common.button.select')}
              value={bulletinDiv}
              onChange={value => onSetBulletinDiv(value)}
            >
              {
                BR0014?.map((item: any) => (
                  <Select.Option
                    key={item.detailCode}
                    value={item.detailCode}
                  >
                    {item.korName}
                  </Select.Option>
                ))
              }
            </Select>
          </div>
        </Descriptions.Item>
        <Descriptions.Item label={`* ${t('homepage.education.column3')}`}>
          <div className="w-150p">
            <Select
              className="w-100"
              placeholder={t('common.button.select')}
              value={bulletinBrn}
              onChange={value => setBulletinBrn(value)}
              disabled={!bulletinDiv}
            >
              {
                bulletinDiv === 'LE'
                  ? BR0015?.map((item: any) => (
                      <Select.Option
                        key={item.detailCode}
                        value={item.detailCode}
                      >
                        {item.korName}
                      </Select.Option>
                    ))
                  : BR0016?.map((item: any) => (
                      <Select.Option
                        key={item.detailCode}
                        value={item.detailCode}
                      >
                        {item.korName}
                      </Select.Option>
                    ))
              }
            </Select>
          </div>
        </Descriptions.Item>
        <Descriptions.Item label={t('homepage.archive.column8')}>
          {moment(serverDate).format('YYYY.MM.DD')}
        </Descriptions.Item>
        <Descriptions.Item label={t('homepage.education.column4')} span={3}>
          <input
            type="text"
            className="form-control"
            value={url}
            onChange={e => setUrl(e.target.value)}
          />
        </Descriptions.Item>
        <Descriptions.Item label={`* ${t('common.column.contents')}`} span={3}>
          <Tinymce
            content={content}
            setContent={setContent}
          />
        </Descriptions.Item>
        <Descriptions.Item label={`* ${t('homepage.education.column5')}`} span={3}>
          {
            thumbnail?.originalName
              ? (
                <div>
                  <a
                    className="download-link"
                    href={`${thumbnail.fileLocation}`}
                    download={thumbnail.originalName}
                  >
                    {thumbnail.originalName}
                  </a>
                  <span
                    className="ml-1 pointer"
                    onClick={() => setThumbnail({ ...thumbnail, originalName: null })}
                  >
                    <CgCloseR />
                  </span>
                </div>
              )
              : null
          }
          <FileUpload
            file={file}
            setFile={(file: File) => setFile(file)}
          />          
        </Descriptions.Item>
      </Descriptions>

      <div className="comm-bx__space">
        <div className="comm-btn__area right">
          <Button
            onClick={onSubmit}
            className="btn-basic pink"
            text={t('common.button.save')}
            writeAuthYn={writeAuthYn}
          />
          <Button
            onClick={() => history.push(`/homepage/education${location.search}`)}
            className="btn-basic gray"
            text={t('common.button.cancel')}
          />
        </div>
      </div>
    </div>
  );
}

export default EducationEditor;
